import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Cookie notice.
//
// Two honest choices, not a dark pattern: "Necessary only" is a real button
// beside "Accept all", not a link hidden behind three menus.
//
// The choice is stored, and what it means is stored with it — so if analytics
// are ever added, the code that adds them can read `cookieConsent` and find a
// decision already made rather than assuming consent.
//
// Deliberately NOT blocking: this app sets exactly one cookie-equivalent today
// (the auth token in localStorage), which is necessary by any definition, so a
// full-screen modal demanding consent before the page can be read would be
// theatre. It sits at the bottom until answered.
// ---------------------------------------------------------------------------

const KEY = 'cookieConsent';

/** What the rest of the app can ask. */
export const cookieConsent = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; }
};

export default function CookieNotice() {
  const [answered, setAnswered] = useState(true);
  const box = useRef(null);

  useEffect(() => {
    // Read in an effect, not in useState, so a server-rendered or
    // storage-blocked environment cannot throw during the first render.
    setAnswered(Boolean(cookieConsent()));
  }, []);

  // IT IS FIXED TO THE BOTTOM, SO IT SITS ON TOP OF THE PAGE.
  //
  // On a phone this is 245px of it, and what was underneath was whatever
  // happened to be at the bottom of the screen: the "Create account" button
  // and the password field on signup, both call-to-action buttons on the home
  // page. Tapping them did nothing, because the tap landed on the notice.
  //
  // Scrolling was not the answer either — the notice is fixed, so it follows,
  // and on a short page there is nothing left to scroll. Reserving the space
  // at the bottom of the document is what actually lets the page move clear
  // of it. Removed the moment the notice is answered.
  useEffect(() => {
    if (answered) return undefined;
    const apply = () => {
      const height = box.current?.getBoundingClientRect().height || 0;
      document.body.style.paddingBottom = `${Math.ceil(height)}px`;
    };
    apply();
    // It wraps differently as the screen turns, so the reservation follows it.
    const observer = new ResizeObserver(apply);
    if (box.current) observer.observe(box.current);
    window.addEventListener('resize', apply);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', apply);
      document.body.style.paddingBottom = '';
    };
  }, [answered]);

  const choose = (analytics) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        necessary: true,
        analytics,
        at: new Date().toISOString()
      }));
    } catch { /* private mode — the banner reappears next visit, which is fine */ }
    setAnswered(true);
  };

  if (answered) return null;

  return (
    <div
      role="region"
      aria-label="Cookie choices"
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4"
      ref={box}
    >
      <div className="max-w-4xl mx-auto rounded-2xl border border-line bg-surface shadow-card
                      p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500
                         flex items-center justify-center shrink-0">
          <Cookie size={18} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">We use cookies</p>
          <p className="text-xs text-ink-2 mt-0.5 leading-relaxed">
            Necessary cookies keep you signed in and are always on. Everything else is optional and
            off until you say otherwise.{' '}
            <Link to="/privacy" className="font-semibold text-brand-500 hover:underline">
              Privacy policy
            </Link>
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => choose(false)}
            className="text-xs font-semibold rounded-lg border-[1px] border-line px-3.5 py-2
                       hover:bg-surface-3 transition whitespace-nowrap"
          >
            Necessary only
          </button>
          <button
            type="button"
            onClick={() => choose(true)}
            className="text-xs font-semibold rounded-lg bg-brand-gradient text-white px-4 py-2
                       hover:shadow-glow transition whitespace-nowrap"
          >
            Accept all cookies
          </button>
          <button
            type="button"
            onClick={() => choose(false)}
            aria-label="Dismiss — necessary cookies only"
            title="Dismiss — necessary cookies only"
            className="p-2.5 -m-1 text-ink-3 hover:text-ink rounded-lg hover:bg-surface-3 transition"
          >
            <X size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
